import { FC } from 'react';
import { RadioGroup, Radio, Form } from 'rsuite';
import { RsNumberInput } from 'components/Rsuite/Input';
import { RunningPattern, BudgetType } from 'core/rtbCampaign/singlePage/common';
import { numberWithCommas } from 'utils/StringUtil';
import styles from './budgetMode.module.scss';
type BudgetPrices = Record<BudgetType, string>;

interface BudgetChoiceProps {
  budgetType: BudgetType;
  onBudgetTypeChange: (v: BudgetType) => void;
  budget: string;
  dailyTargetBudget: string;
  onBudgetChange: (v: BudgetPrices) => void;
  runningPattern: string;
  dailyErrorMessage: string | undefined;
  totalErrorMessage: string | undefined;
  disabled?: boolean;
  disabledText?: string;
  numberOfProducts: number;
  diffDays: number;
}

export const BudgetMode: FC<BudgetChoiceProps> = ({
  budgetType,
  onBudgetTypeChange,
  budget,
  dailyTargetBudget,
  onBudgetChange,
  runningPattern,
  dailyErrorMessage,
  totalErrorMessage,
  disabled = false,
  disabledText = '',
  numberOfProducts,
  diffDays,
}) => {
  const dateRange = runningPattern === RunningPattern.UNLIMITED ? 14 : diffDays;

  const suggestedDailyBudget = (
    <>
      根據商品設定與走期，建議日預算 &gt;{' '}
      {numberWithCommas(
        Math.ceil(((numberOfProducts || 0) * 2500) / dateRange),
      )}
      <br />
      您設定的總預算預估為 ${numberWithCommas(Number(budget || 0).toFixed(2))}
    </>
  );

  const suggestedTotalBudget = (
    <>
      <span>
        根據商品設定與走期，建議總預算 &gt;{' '}
        {numberWithCommas(Math.ceil(numberOfProducts * 2500))}
      </span>
      <br />
      <span>預算消耗速度依競爭情況浮動，可能於走期結束前消耗完畢</span>
    </>
  );
  const budgetData = {
    [BudgetType.DAILY]: {
      value: BudgetType.DAILY,
      label: '設定每日預算',
      disabled: budgetType === BudgetType.TOTAL && disabled,
      hint: disabled
        ? budgetType === BudgetType.DAILY
          ? suggestedDailyBudget
          : disabledText
        : suggestedDailyBudget,
      errorMessage: dailyErrorMessage || totalErrorMessage,
    },
    [BudgetType.TOTAL]: {
      value: BudgetType.TOTAL,
      label: '設定活動總預算',
      disabled: budgetType === BudgetType.DAILY && disabled,
      hint: disabled
        ? budgetType === BudgetType.TOTAL
          ? suggestedTotalBudget
          : disabledText
        : suggestedTotalBudget,
      errorMessage: totalErrorMessage,
    },
  };

  const budgetTypes =
    runningPattern === RunningPattern.UNLIMITED
      ? [BudgetType.DAILY]
      : [BudgetType.DAILY, BudgetType.TOTAL];
  const data = budgetTypes.map((budgetType) => budgetData[budgetType]);

  const budgets = {
    [BudgetType.DAILY]: dailyTargetBudget,
    [BudgetType.TOTAL]: budget,
  };

  const handleBudgetTypeChange = (newBudgetType) => {
    onBudgetTypeChange(newBudgetType);
  };
  const handleBudgetChange = (newBudgets) => {
    onBudgetChange(newBudgets);
  };

  return (
    <RadioGroup
      value={budgetType}
      onChange={handleBudgetTypeChange}
      className={styles['radio-group-wrapper']}
      inline
    >
      {data.map(({ value, label, hint, disabled, errorMessage }) => {
        const inputDisabled = value !== budgetType;

        return (
          <div key={value} className={styles['budget-container']}>
            <div className={styles['budget__item-wrapper']}>
              <div>
                <Radio
                  value={value}
                  disabled={disabled}
                  className={styles.budget__radio}
                >
                  {label}
                </Radio>
              </div>
              {!inputDisabled && (
                <div className={styles.budget__input}>
                  <RsNumberInput
                    prefix='NTD'
                    num={budgets[value]}
                    onChange={(newPrice) =>
                      handleBudgetChange({ type: value, newPrice })
                    }
                    placeholder='請輸入金額' // i18n
                    disabled={inputDisabled}
                  />
                  <Form.ErrorMessage
                    show={errorMessage !== undefined}
                    placement='topEnd'
                  >
                    {errorMessage}
                  </Form.ErrorMessage>
                </div>
              )}
            </div>
            <div className={styles.budget__hint}>
              {disabled || !inputDisabled ? (
                <Form.HelpText>{hint}</Form.HelpText>
              ) : null}
            </div>
          </div>
        );
      })}
    </RadioGroup>
  );
};
